import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Crown, Building2, Users, Activity, KeyRound, Copy, ShieldCheck, Mail, AlertCircle, CheckCircle2, ChevronRight, BookOpen, CreditCard, PauseCircle, PlayCircle, Pencil, UserCheck, Clock, UserX, CalendarPlus, ToggleLeft } from 'lucide-react';
import { toast } from 'sonner';
import SeedTrialCard from '../components/SeedTrialCard';

const PLAN_BADGE = {
  starter:    'bg-slate-100 text-slate-700 border-slate-200',
  standard:   'bg-sky-100 text-sky-800 border-sky-200',
  pro:        'bg-amber-100 text-amber-800 border-amber-200',
  enterprise: 'bg-violet-100 text-violet-800 border-violet-200',
};

export default function SuperAdminDashboard() {
  const { user } = useContext(AuthContext);
  const [overview, setOverview] = useState([]);
  const [help, setHelp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetResult, setResetResult] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  // Phase 19.1 — Subscriptions management
  const [subs, setSubs] = useState([]);
  const [editSub, setEditSub] = useState(null);
  const [editForm, setEditForm] = useState({ plan: '', subscription_status: '', subscription_renewal_due: '' });
  // Phase 26 — Trial approvals
  const [trialUsers, setTrialUsers] = useState([]);
  const [trialFilter, setTrialFilter] = useState('pending'); // pending | expired | approved | all
  const [approveUser, setApproveUser] = useState(null);
  const [approveRole, setApproveRole] = useState('ADMIN');
  // Phase 39a — per-company feature toggles (Enterprise add-on overrides)
  const [featSub, setFeatSub] = useState(null);
  const [featDraft, setFeatDraft] = useState({});
  const openFeatures = (s) => {
    setFeatSub(s);
    setFeatDraft({
      purchase_module: !!s.features?.purchase_module,
      sales_module: !!s.features?.sales_module,
      accounting_module: !!s.features?.accounting_module,
      ai_insights: !!s.features?.ai_insights,
      amc_contracts: !!s.features?.amc_contracts,
      tenant_portal: !!s.features?.tenant_portal,
      hr: !!s.features?.hr,
    });
  };
  const saveFeatures = async () => {
    if (!featSub) return;
    try {
      await axios.put(`${API}/admin/subscriptions/${featSub.company_id}`, { features: featDraft }, { withCredentials: true });
      toast.success('Feature overrides saved');
      setFeatSub(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const isSA = user?.role === 'SUPERADMIN';

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [o, h, s, t] = await Promise.all([
        axios.get(`${API}/superadmin/overview`, { withCredentials: true }),
        axios.get(`${API}/superadmin/credentials-help`, { withCredentials: true }),
        axios.get(`${API}/admin/subscriptions`, { withCredentials: true }),
        axios.get(`${API}/superadmin/trial-users`, { params: { show: 'all' }, withCredentials: true }),
      ]);
      setOverview(o.data || []);
      setHelp(h.data);
      setSubs(s.data || []);
      setTrialUsers(t.data?.users || []);
    } catch { toast.error('Could not load Super Admin overview'); }
    setLoading(false);
  };

  const approveTrial = async () => {
    if (!approveUser) return;
    try {
      await axios.post(`${API}/superadmin/users/${approveUser.id}/approve`,
        { role: approveRole }, { withCredentials: true });
      toast.success(`Approved ${approveUser.email} as ${approveRole}`);
      setApproveUser(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
  };

  const extendTrial = async (u) => {
    const daysStr = window.prompt(`Extend trial of ${u.email} by how many days?`, '7');
    if (!daysStr) return;
    const days = parseInt(daysStr, 10);
    if (!days || days < 1 || days > 90) { toast.error('Enter 1–90 days'); return; }
    try {
      await axios.post(`${API}/superadmin/users/${u.id}/extend-trial`,
        { days }, { withCredentials: true });
      toast.success(`Trial extended by ${days} day(s)`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Extend failed'); }
  };

  const rejectTrial = async (u) => {
    if (!window.confirm(`Reject and block ${u.email}? Their sessions will be revoked.`)) return;
    try {
      await axios.post(`${API}/superadmin/users/${u.id}/reject`, {}, { withCredentials: true });
      toast.success(`Rejected ${u.email}`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  useEffect(() => { if (isSA) fetchAll(); }, [isSA]);

  const resetTempPassword = async (initialAdmin, companyName) => {
    if (!initialAdmin) return;
    if (!window.confirm(`Mint a fresh temporary password for ${initialAdmin.email}? Their existing sessions will be revoked.`)) return;
    try {
      const r = await axios.post(`${API}/users/${initialAdmin.id}/reset-password`, {}, { withCredentials: true });
      setResetResult({ email: initialAdmin.email, company: companyName, temp_password: r.data.temp_password });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  };

  const copyToClipboard = async (txt, label) => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label} copied`); }
    catch { toast.error('Copy failed'); }
  };

  // Phase 19.1 — subscription mutations
  const openEditSub = (s) => {
    setEditSub(s);
    setEditForm({
      plan: s.plan || 'standard',
      subscription_status: s.subscription_status || 'active',
      subscription_renewal_due: s.subscription_renewal_due ? s.subscription_renewal_due.slice(0, 10) : '',
    });
  };
  const saveSubEdit = async () => {
    if (!editSub) return;
    const body = {};
    if (editForm.plan) body.plan = editForm.plan;
    if (editForm.subscription_status) body.subscription_status = editForm.subscription_status;
    if (editForm.subscription_renewal_due) body.subscription_renewal_due = editForm.subscription_renewal_due + 'T00:00:00Z';
    try {
      await axios.put(`${API}/admin/subscriptions/${editSub.company_id}`, body, { withCredentials: true });
      toast.success('Subscription updated');
      setEditSub(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };
  const quickToggle = async (s, nextStatus) => {
    try {
      await axios.put(`${API}/admin/subscriptions/${s.company_id}`, { subscription_status: nextStatus }, { withCredentials: true });
      toast.success(`Status → ${nextStatus}`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  if (!isSA) return <Layout><div className="p-6 text-center text-muted-foreground">Super Admin only.</div></Layout>;

  const totals = overview.reduce((acc, r) => ({
    companies: acc.companies + 1,
    users: acc.users + r.user_count,
    open_complaints: acc.open_complaints + r.open_complaints,
    paid_total_usd: acc.paid_total_usd + r.paid_total_usd,
  }), { companies: 0, users: 0, open_complaints: 0, paid_total_usd: 0 });

  return (
    <Layout>
      <div data-testid="superadmin-dash-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2">
              <Crown className="w-6 h-6 text-amber-600" />Super Admin Dashboard
            </h1>
            <p className="text-sm text-muted-foreground">Tenant directory — view every subscriber company, their initial admin, and credential lifecycle.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" data-testid="cred-help-btn" onClick={() => setHelpOpen(true)} className="border-amber-300 text-amber-700"><KeyRound className="w-4 h-4 mr-1" />How credentials work</Button>
            <Button data-testid="onboard-cta" asChild className="bg-amber-500 hover:bg-amber-600 text-slate-900"><a href="/superadmin/onboard-company"><BookOpen className="w-4 h-4 mr-1" />Onboard new company</a></Button>
          </div>
        </div>

        {/* Phase 37.3 — Prominent Seed-Trial-Accounts widget so SA can provision
            evaluator credentials with one click on ANY environment (esp. after
            a fresh production deploy). Fixes the "trial passwords not working"
            confusion where preview accounts don't exist on production DB. */}
        <SeedTrialCard variant="compact" />

        {/* Top KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4"><div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />Companies</div><div className="text-2xl font-bold text-foreground">{totals.companies}</div></Card>
          <Card className="p-4"><div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1"><Users className="w-3.5 h-3.5" />Total users</div><div className="text-2xl font-bold text-foreground">{totals.users}</div></Card>
          <Card className="p-4"><div className="text-xs uppercase tracking-wide text-rose-600 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />Open complaints</div><div className="text-2xl font-bold text-rose-700">{totals.open_complaints}</div></Card>
          <Card className="p-4"><div className="text-xs uppercase tracking-wide text-emerald-600 flex items-center gap-1"><Activity className="w-3.5 h-3.5" />Stripe revenue</div><div className="text-2xl font-bold text-emerald-700">${totals.paid_total_usd.toLocaleString()}</div></Card>
        </div>

        {/* ── Phase 26 — Trial Signups & Approvals ── */}
        <Card data-testid="trial-approvals-card" className="p-0 overflow-hidden border-amber-200">
          <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 dark:from-amber-900/20 dark:via-yellow-900/10 dark:to-amber-900/20 px-4 py-3 border-b border-amber-200/60 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-amber-700" />
              <div>
                <div className="font-semibold text-foreground dark:text-slate-100">Trial Signups &amp; Approvals</div>
                <div className="text-xs text-slate-600 dark:text-slate-300">Self-signups land here on a 7-day trial. Approve to grant permanent access, extend the trial, or reject.</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {[
                { v: 'pending', l: 'Active trials', cls: 'bg-amber-500 text-white' },
                { v: 'expired', l: 'Expired', cls: 'bg-rose-500 text-white' },
                { v: 'approved', l: 'Approved', cls: 'bg-emerald-500 text-white' },
                { v: 'all', l: 'All', cls: 'bg-slate-500 text-white' },
              ].map(t => {
                const count = trialUsers.filter(u => {
                  if (t.v === 'pending') return u.is_trial && !u.approved_by_superadmin && !u.expired;
                  if (t.v === 'expired') return u.is_trial && !u.approved_by_superadmin && u.expired;
                  if (t.v === 'approved') return u.approved_by_superadmin;
                  return true;
                }).length;
                return (
                  <button key={t.v} data-testid={`trial-filter-${t.v}`}
                    onClick={() => setTrialFilter(t.v)}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium transition ${trialFilter === t.v ? t.cls : 'bg-card text-foreground border border-border hover:bg-muted'}`}>
                    {t.l} <span className="opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-800 border-b">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2">Workspace</th>
                  <th className="px-4 py-2">Signed up</th>
                  <th className="px-4 py-2">Trial state</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {trialUsers
                  .filter(u => {
                    if (trialFilter === 'pending') return u.is_trial && !u.approved_by_superadmin && !u.expired;
                    if (trialFilter === 'expired') return u.is_trial && !u.approved_by_superadmin && u.expired;
                    if (trialFilter === 'approved') return u.approved_by_superadmin;
                    return true;
                  })
                  .map((u, idx) => (
                    <tr key={u.id} data-testid={`trial-row-${idx}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          {u.picture
                            ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                            : <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">{(u.name || u.email)?.[0]?.toUpperCase()}</div>}
                          <div>
                            <div className="font-medium text-slate-900 dark:text-slate-100">{u.name}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {u.company ? <>
                          <div>{u.company.name}</div>
                          <div className="text-slate-400">{u.company.plan} · {u.company.subscription_paid ? <span className="text-emerald-600">paid</span> : 'unpaid'}</div>
                        </> : '—'}
                      </td>
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {u.approved_by_superadmin
                          ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" /> Approved</span>
                          : u.expired
                            ? <span className="inline-flex items-center gap-1 text-rose-700"><Clock className="w-3 h-3" /> Trial expired {u.trial_ends_at ? `(${new Date(u.trial_ends_at).toLocaleDateString()})` : ''}</span>
                            : <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="w-3 h-3" /> {u.days_remaining} day(s) remaining</span>}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {u.is_blocked
                          ? <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-700">Rejected</span>
                          : <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">{u.role}</span>}
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap">
                        {!u.approved_by_superadmin && !u.is_blocked && (
                          <>
                            <Button data-testid={`trial-approve-${idx}`} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white"
                              onClick={() => { setApproveUser(u); setApproveRole('ADMIN'); }}>
                              <UserCheck className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                            <Button data-testid={`trial-extend-${idx}`} size="sm" variant="outline" className="ml-1" onClick={() => extendTrial(u)}>
                              <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Extend
                            </Button>
                            <Button data-testid={`trial-reject-${idx}`} size="sm" variant="ghost" className="text-rose-600 ml-1" onClick={() => rejectTrial(u)}>
                              <UserX className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                {trialUsers.filter(u => {
                  if (trialFilter === 'pending') return u.is_trial && !u.approved_by_superadmin && !u.expired;
                  if (trialFilter === 'expired') return u.is_trial && !u.approved_by_superadmin && u.expired;
                  if (trialFilter === 'approved') return u.approved_by_superadmin;
                  return true;
                }).length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-xs" data-testid="trial-empty">
                    No users in this view.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Approve role-picker dialog */}
        <Dialog open={!!approveUser} onOpenChange={(v) => !v && setApproveUser(null)}>
          <DialogContent data-testid="approve-dialog" className="max-w-md">
            <DialogHeader><DialogTitle>Approve {approveUser?.email}</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div className="text-sm text-muted-foreground dark:text-slate-400">
                Granting permanent access. Normally choose <strong>ADMIN</strong> so they can invite &amp; manage their own company users via RBAC.
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Role to assign</label>
                <select data-testid="approve-role-select"
                  value={approveRole}
                  onChange={(e) => setApproveRole(e.target.value)}
                  className="w-full h-9 px-2 mt-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm">
                  <option value="ADMIN">ADMIN (recommended — Company Admin)</option>
                  <option value="CEO">CEO</option>
                  <option value="FULL_ACCESS">FULL_ACCESS</option>
                  <option value="MANAGER">MANAGER</option>
                  <option value="ACCOUNTANT">ACCOUNTANT</option>
                  <option value="PROCUREMENT_OFFICER">PROCUREMENT OFFICER</option>
                  <option value="HR_MANAGER">HR MANAGER</option>
                  <option value="STOREKEEPER">STOREKEEPER</option>
                  <option value="SECURITY_OFFICER">SECURITY OFFICER</option>
                  <option value="CS_SUPERVISOR">CS_SUPERVISOR</option>
                  <option value="TECHNICIAN">TECHNICIAN</option>
                  <option value="TENANT">TENANT</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setApproveUser(null)}>Cancel</Button>
              <Button data-testid="approve-confirm-btn" onClick={approveTrial} className="bg-emerald-500 hover:bg-emerald-600 text-white">
                <UserCheck className="w-4 h-4 mr-1" /> Approve as {approveRole}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Companies table */}
        <Card className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3">Users</th>
                <th className="px-4 py-3">Initial Admin</th>
                <th className="px-4 py-3">Credential Status</th>
                <th className="px-4 py-3">Last Login</th>
                <th className="px-4 py-3 text-right">Paid (USD)</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="8" className="text-center py-12 text-slate-500">Loading…</td></tr> :
              overview.length === 0 ? <tr><td colSpan="8" className="text-center py-12 text-slate-500">No companies yet — onboard one to start.</td></tr> :
              overview.map(r => {
                const c = r.company;
                const adm = r.initial_admin;
                const status = !adm ? 'no_admin' : adm.is_blocked ? 'blocked' : adm.must_change_password ? 'temp_pwd' : 'activated';
                return (
                  <tr key={c.id} data-testid={`sa-company-${c.id}`} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-foreground">{c.name}</div>
                      <div className="text-xs text-muted-foreground font-mono">{c.id.slice(0, 8)}…</div>
                    </td>
                    <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full uppercase border ${PLAN_BADGE[c.plan] || PLAN_BADGE.starter}`}>{c.plan}</span></td>
                    <td className="px-4 py-3 text-slate-700">{r.user_count}<span className="text-xs text-muted-foreground"> ({r.admin_count} admin{r.admin_count !== 1 ? 's' : ''})</span></td>
                    <td className="px-4 py-3">
                      {adm ? (
                        <div>
                          <div className="font-medium text-slate-900">{adm.name}</div>
                          <button type="button" onClick={() => copyToClipboard(adm.email, 'Email')} className="text-xs text-amber-700 hover:underline inline-flex items-center gap-1"><Mail className="w-3 h-3" />{adm.email}</button>
                          <div className="text-[10px] uppercase tracking-wide text-slate-400">{adm.role} · {adm.auth_provider}</div>
                        </div>
                      ) : <span className="text-xs text-rose-600">No admin yet</span>}
                    </td>
                    <td className="px-4 py-3">
                      {status === 'temp_pwd'   && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"><KeyRound className="w-3 h-3" />Temp password</span>}
                      {status === 'activated'  && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"><CheckCircle2 className="w-3 h-3" />Activated</span>}
                      {status === 'blocked'    && <span className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full"><AlertCircle className="w-3 h-3" />Blocked</span>}
                      {status === 'no_admin'   && <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">No admin</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700">
                      {r.last_login ? (
                        <>
                          <div>{new Date(r.last_login).toLocaleString()}</div>
                          <div className="text-muted-foreground">{r.last_login_user}</div>
                        </>
                      ) : <span className="text-slate-400">Never</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-700 font-medium">${r.paid_total_usd.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        {adm && (
                          <Button data-testid={`reset-pwd-${c.id}`} size="sm" variant="outline" onClick={() => resetTempPassword(adm, c.name)} className="border-amber-300 text-amber-700"><KeyRound className="w-3.5 h-3.5 mr-1" />Reset Pwd</Button>
                        )}
                        <Button data-testid={`view-company-${c.id}`} size="sm" variant="outline" asChild>
                          <a href={`/users`}><ChevronRight className="w-3.5 h-3.5" /></a>
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        {/* Phase 19.1 — Subscriptions management */}
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b bg-gradient-to-r from-amber-50 to-yellow-50 flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold text-foreground">
              <CreditCard className="w-4 h-4 text-amber-600" />Subscriptions
              <span className="text-xs font-normal text-slate-500">· Stripe payments + manual overrides</span>
            </div>
            <div className="text-xs text-muted-foreground">
              {subs.filter(s => s.subscription_status === 'active').length} active · {subs.filter(s => s.subscription_status === 'hold').length} on hold
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Buyer</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Renewal Due</th>
                  <th className="px-4 py-3 text-right">Last Paid (USD)</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan="8" className="text-center py-10 text-slate-500">Loading…</td></tr> :
                 subs.length === 0 ? <tr><td colSpan="8" className="text-center py-10 text-slate-500">No subscriptions yet.</td></tr> :
                 subs.map(s => {
                   const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
                   const overdue = s.subscription_renewal_due && new Date(s.subscription_renewal_due) < new Date();
                   return (
                     <tr key={s.company_id} data-testid={`sa-sub-${s.company_id}`} className="border-b last:border-0 hover:bg-slate-50">
                       <td className="px-4 py-3"><div className="font-semibold text-foreground">{s.company_name}</div><div className="text-xs text-muted-foreground font-mono">{s.company_id.slice(0, 8)}…</div></td>
                       <td className="px-4 py-3 text-xs">
                         {s.buyer ? <><div className="font-medium text-slate-800">{s.buyer.name}</div><div className="text-muted-foreground">{s.buyer.email}</div>{s.buyer.phone && <div className="text-muted-foreground">☎ {s.buyer.phone}</div>}</> : <span className="text-slate-400">—</span>}
                       </td>
                       <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full uppercase border ${PLAN_BADGE[s.plan] || PLAN_BADGE.starter}`}>{s.plan}</span></td>
                       <td className="px-4 py-3">
                         <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${
                           s.subscription_status === 'active'    ? 'text-emerald-700 bg-emerald-50 border-emerald-200' :
                           s.subscription_status === 'hold'      ? 'text-amber-700 bg-amber-50 border-amber-200' :
                           s.subscription_status === 'cancelled' ? 'text-rose-700 bg-rose-50 border-rose-200' :
                                                                   'text-slate-700 bg-slate-100 border-slate-200'
                         }`}>
                           {s.subscription_status}
                         </span>
                       </td>
                       <td className="px-4 py-3 text-xs text-slate-700">{fmtDate(s.subscription_started_at)}</td>
                       <td className="px-4 py-3 text-xs"><span className={overdue ? 'text-rose-700 font-semibold' : 'text-slate-700'}>{fmtDate(s.subscription_renewal_due)}{overdue && ' ⚠'}</span></td>
                       <td className="px-4 py-3 text-right text-emerald-700 font-medium">${(s.last_amount_usd || 0).toFixed(2)}</td>
                       <td className="px-4 py-3">
                         <div className="flex flex-wrap items-center gap-1">
                           {s.subscription_status === 'active' ? (
                             <Button data-testid={`hold-sub-${s.company_id}`} size="sm" variant="outline" onClick={() => quickToggle(s, 'hold')} className="border-amber-300 text-amber-700"><PauseCircle className="w-3.5 h-3.5 mr-1" />Hold</Button>
                           ) : (
                             <Button data-testid={`activate-sub-${s.company_id}`} size="sm" variant="outline" onClick={() => quickToggle(s, 'active')} className="border-emerald-300 text-emerald-700"><PlayCircle className="w-3.5 h-3.5 mr-1" />Activate</Button>
                           )}
                           <Button data-testid={`edit-sub-${s.company_id}`} size="sm" variant="outline" onClick={() => openEditSub(s)}><Pencil className="w-3.5 h-3.5" /></Button>
                          <Button data-testid={`features-sub-${s.company_id}`} size="sm" variant="outline" onClick={() => openFeatures(s)} className="border-violet-300 text-violet-700"><ToggleLeft className="w-3.5 h-3.5 mr-1" />Features</Button>
                         </div>
                       </td>
                     </tr>
                   );
                 })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Edit subscription dialog */}
        {editSub && (
          <Dialog open onOpenChange={() => setEditSub(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-amber-600" />Edit subscription — {editSub.company_name}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Plan</label>
                  <select data-testid="edit-sub-plan" value={editForm.plan} onChange={(e) => setEditForm({ ...editForm, plan: e.target.value })} className="w-full mt-1 border rounded px-3 py-2 text-sm">
                    <option value="starter">Starter (free)</option>
                    <option value="standard">Standard — $29/mo</option>
                    <option value="pro">Pro — $79/mo</option>
                    <option value="enterprise">Enterprise — custom</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Status</label>
                  <select data-testid="edit-sub-status" value={editForm.subscription_status} onChange={(e) => setEditForm({ ...editForm, subscription_status: e.target.value })} className="w-full mt-1 border rounded px-3 py-2 text-sm">
                    <option value="active">Active</option>
                    <option value="hold">Hold</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Renewal due</label>
                  <input type="date" data-testid="edit-sub-renewal" value={editForm.subscription_renewal_due} onChange={(e) => setEditForm({ ...editForm, subscription_renewal_due: e.target.value })} className="w-full mt-1 border rounded px-3 py-2 text-sm" />
                </div>
                <div className="flex gap-2 justify-end pt-2 border-t">
                  <Button variant="outline" onClick={() => setEditSub(null)}>Cancel</Button>
                  <Button data-testid="save-sub-edit" onClick={saveSubEdit} className="bg-amber-500 hover:bg-amber-600 text-slate-900">Save override</Button>
                </div>
                <p className="text-xs text-muted-foreground">This override is logged for audit. The customer keeps their access based on the new status + renewal date.</p>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Phase 39a — Per-company feature toggles (SuperAdmin override) */}
        {featSub && (
          <Dialog open onOpenChange={() => setFeatSub(null)}>
            <DialogContent className="max-w-lg" data-testid="feature-toggle-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ToggleLeft className="w-5 h-5 text-violet-600" />
                  Feature overrides — {featSub.company_name}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-2 mt-3">
                <p className="text-xs text-muted-foreground">
                  Grant any add-on to this company regardless of their plan (<strong>{featSub.plan}</strong>).
                  Great for pilot customers, VIPs, or when a client pre-pays for one module.
                </p>
                {[
                  { key: 'purchase_module', label: 'Purchase module', hint: 'LPO with 3 supplier quotes, GRN with partial receipts, supplier dashboard' },
                  { key: 'sales_module', label: 'Sales module', hint: 'Sales quotations, invoices, AR aging' },
                  { key: 'accounting_module', label: 'Accounting module', hint: 'Vouchers, P&L, chart of accounts, banks' },
                  { key: 'ai_insights', label: 'AI insights', hint: 'Claude Sonnet — predictive maintenance & report narratives' },
                  { key: 'amc_contracts', label: 'AMC contracts', hint: 'Annual maintenance contracts + provider KPI' },
                  { key: 'tenant_portal', label: 'Tenant portal', hint: 'Tenant self-service (complaints, utility readings)' },
                  { key: 'hr', label: 'HR & payroll', hint: 'Payroll, attendance, employee records' },
                ].map(f => (
                  <label key={f.key} data-testid={`feature-toggle-${f.key}`}
                         className="flex items-start gap-3 p-3 rounded border hover:bg-violet-50 cursor-pointer">
                    <input type="checkbox" checked={!!featDraft[f.key]}
                           onChange={(e) => setFeatDraft({ ...featDraft, [f.key]: e.target.checked })}
                           className="mt-0.5 w-4 h-4 accent-violet-600" />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{f.label}</div>
                      <div className="text-[11px] text-muted-foreground">{f.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex gap-2 justify-end pt-3 border-t mt-2">
                <Button variant="outline" onClick={() => setFeatSub(null)}>Cancel</Button>
                <Button data-testid="save-feature-toggles" onClick={saveFeatures}
                        className="bg-violet-600 hover:bg-violet-700 text-white">Save overrides</Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Change is logged for audit. Company sees the new modules in their sidebar immediately.</p>
            </DialogContent>
          </Dialog>
        )}


        {/* Reset result dialog (one-time password reveal) */}
        {resetResult && (
          <Dialog open onOpenChange={() => setResetResult(null)}>
            <DialogContent>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-amber-600" />Temp Password Issued</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <p className="text-sm text-muted-foreground">Share these credentials with <strong>{resetResult.email}</strong> ({resetResult.company}) via your secure channel. The password will <em>not be shown again</em>.</p>
                <Card className="p-4 bg-amber-50 border border-amber-200 space-y-2">
                  <div className="flex items-center justify-between">
                    <div><div className="text-xs text-muted-foreground">Email</div><div className="font-mono text-sm">{resetResult.email}</div></div>
                    <Button size="sm" variant="ghost" onClick={() => copyToClipboard(resetResult.email, 'Email')}><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div><div className="text-xs text-muted-foreground">Temp Password</div><div data-testid="reset-temp-pwd-value" className="font-mono text-base font-bold text-amber-900">{resetResult.temp_password}</div></div>
                    <Button data-testid="copy-temp-pwd" size="sm" variant="ghost" onClick={() => copyToClipboard(resetResult.temp_password, 'Password')}><Copy className="w-3.5 h-3.5" /></Button>
                  </div>
                </Card>
                <Button onClick={() => setResetResult(null)} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900">Done — I have copied it</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Credentials help dialog */}
        {helpOpen && help && (
          <Dialog open onOpenChange={setHelpOpen}>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>How credentials reach a new tenant</DialogTitle></DialogHeader>
              <ol className="space-y-3 mt-2 text-sm">
                {help.flow.map(s => (
                  <li key={s.step} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-amber-500 text-slate-900 flex items-center justify-center font-bold text-xs flex-shrink-0">{s.step}</div>
                    <div><div className="font-semibold text-foreground">{s.title}</div><div className="text-muted-foreground">{s.detail}</div></div>
                  </li>
                ))}
              </ol>
              <div className="mt-4 pt-3 border-t text-xs text-muted-foreground space-y-1">
                {help.tips.map((t, i) => <div key={i} className="flex gap-2"><span className="text-amber-600">•</span><span>{t}</span></div>)}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}
